import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Truck, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertModal from '../components/AlertModal';
import { orderAPI, userAPI } from '../utils/api';

function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const cart = location.state?.cart;
  const products = location.state?.products || {};

  const [address, setAddress] = useState({
    recipient: '',
    phone: '',
    postal_code: '',
    address: '',
    detail_address: '',
    request: '',
  });
  const [loading, setLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });

  // 저장된 유저 정보 불러오기 (이름, 전화번호는 user에서, 주소는 saved_address에서)
  useEffect(() => {
    const loadSavedAddress = async () => {
      try {
        const profile = await userAPI.getProfile();
        setAddress((prev) => ({
          ...prev,
          recipient: profile.name || '',
          phone: profile.phone || '',
          postal_code: profile.saved_address?.postal_code || '',
          address: profile.saved_address?.address || '',
          detail_address: profile.saved_address?.detail_address || '',
          request: profile.saved_address?.request || '',
        }));
      } catch (err) {
        // 저장된 주소가 없거나 로드 실패 시 무시
      }
    };
    loadSavedAddress();
  }, []);

  const showAlert = (type, message) => {
    setAlertModal({ isOpen: true, type, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', message: '' });
  };

  if (!cart) {
    navigate('/cart');
    return null;
  }

  const handleSearchAddress = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        setAddress(prev => ({
          ...prev,
          postal_code: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
      }
    }).open();
  };

  const handleCheckout = async () => {
    if (!address.recipient || !address.phone || !address.postal_code || !address.address) {
      showAlert('error', '배송 정보를 모두 입력해주세요.');
      return;
    }

    try {
      setLoading(true);
      const result = await orderAPI.checkout(cart.uuid, address);
      window.location.href = result.payment_url;
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">주문/결제</h1>

        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 배송 정보 */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <Truck className="w-5 h-5 text-teal-500" />
                <h2 className="text-lg font-bold text-gray-900">배송 정보</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">받는 사람</label>
                  <input
                    type="text"
                    value={address.recipient}
                    onChange={(e) => setAddress({ ...address, recipient: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="홍길동"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">전화번호</label>
                  <input
                    type="tel"
                    value={address.phone}
                    onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="01012345678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">우편번호</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={address.postal_code}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                      placeholder="우편번호"
                    />
                    <button
                      type="button"
                      onClick={handleSearchAddress}
                      className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 whitespace-nowrap"
                    >
                      <Search className="w-4 h-4" />
                      주소 검색
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                  <input
                    type="text"
                    value={address.address}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                    placeholder="주소 검색 버튼을 눌러주세요"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">상세주소</label>
                  <input
                    type="text"
                    value={address.detail_address}
                    onChange={(e) => setAddress({ ...address, detail_address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="101동 101호"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">배송 요청사항</label>
                  <textarea
                    value={address.request}
                    onChange={(e) => setAddress({ ...address, request: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    rows="3"
                    placeholder="문 앞에 놔주세요."
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* 결제 금액 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-6 sticky top-24">
              {/* 주문 상품 목록 */}
              {cart.items && cart.items.length > 0 && (
                <div className="mb-4">
                  <h2 className="text-sm font-bold text-gray-700 mb-2">주문 상품</h2>
                  <div className="space-y-1.5 pb-4 border-b border-gray-100">
                    {cart.items.map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-600">
                        <span className="font-medium text-gray-800">
                          {products[item.product_uuid]?.name || '상품'} × {item.quantity}
                        </span>
                        {item.option_name && (
                          <span className="ml-1.5 text-xs bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded">
                            {item.option_name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h2 className="text-lg font-bold text-gray-900 mb-3">결제 금액</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>상품 금액</span>
                  <span className="font-medium text-gray-900">{cart.subtotal?.toLocaleString() || 0}원</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>배송비</span>
                  <span className="font-medium text-gray-900">{cart.shipping_fee?.toLocaleString() || 0}원</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-4">
                  <span className="text-gray-900">총 결제금액</span>
                  <span className="text-teal-600">{cart.total_cost?.toLocaleString() || 0}원</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full bg-teal-500 text-white py-4 rounded-lg text-lg font-semibold hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    처리 중...
                  </>
                ) : (
                  '결제하기'
                )}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        message={alertModal.message}
      />
    </div>
  );
}

export default CheckoutPage;
